import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Delete, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Login({ slug: slugProp }) {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const slug = slugProp || params.slug || "gandlone";
  const [org, setOrg] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [phase, setPhase] = useState("phone"); // phone | pin
  const [phone, setPhone] = useState("");
  const [matched, setMatched] = useState(null); // {account_id, name}
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (user) navigate("/dashboard"); }, [user, navigate]);
  useEffect(() => { api.get(`/orgs/${slug}`).then((r) => setOrg(r.data)).catch(() => setNotFound(true)); }, [slug]);

  const lookup = async (e) => {
    e?.preventDefault();
    if (!phone.trim()) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/lookup", { slug, phone });
      setMatched(data); setPhase("pin"); setPin("");
    } catch (err) {
      setError(err.response?.data?.detail || "That didn't match. Check the number or PIN and try again.");
    }
    setBusy(false);
  };

  const submitPin = async (fullPin) => {
    setBusy(true); setError("");
    try {
      await login(matched.account_id, fullPin);
      toast.success(`G'day, ${matched.name.split(" ")[0]} 👋`);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "That didn't match. Check the number or PIN and try again.");
      setPin("");
    }
    setBusy(false);
  };

  const tap = (d) => {
    if (busy) return;
    const next = (pin + d).slice(0, 4);
    setPin(next);
    if (next.length === 4) submitPin(next);
  };

  const initial = (n) => (n || "?")[0].toUpperCase();

  return (
    <div className="min-h-screen flex flex-col" data-testid="login-page">
      <div className="btn-grad text-white px-6 pt-12 pb-10 rounded-b-[2.5rem]">
        <div className="max-w-md mx-auto">
          <button data-testid="back-to-portal" onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-white/80 text-sm mb-3 hover:text-white"><ArrowLeft size={15} strokeWidth={2.5} /> All systems</button>
          <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-3 py-1 text-sm font-medium mb-4">Ready2Work AU</div>
          <h1 className="font-heading font-extrabold text-4xl tracking-tight leading-tight">G'day 👋</h1>
          <p className="text-white/85 mt-1">{org?.name || (notFound ? "System not found" : "Loading…")} — let's log some kilos.</p>
        </div>
      </div>

      <div className="flex-1 max-w-md w-full mx-auto px-6 -mt-6">
        {phase === "phone" ? (
          <form onSubmit={lookup} className="animate-fade-up bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
            <p className="text-slate-500 text-sm mb-3 font-medium">Enter your mobile number to sign in</p>
            <Input data-testid="login-phone" type="tel" inputMode="numeric" autoFocus placeholder="04xx xxx xxx"
              className="h-14 text-lg font-num rounded-xl tracking-wide" value={phone} onChange={(e) => setPhone(e.target.value)} />
            {error && <p data-testid="login-error" className="text-rose-500 text-sm mt-3">{error}</p>}
            <Button data-testid="login-continue" type="submit" disabled={busy || !phone.trim()} className="w-full h-14 mt-4 btn-grad text-white rounded-2xl text-base">
              {busy ? "Checking…" : "Continue →"}
            </Button>
            {notFound && <p className="text-slate-500 text-sm mt-3">We couldn't find that system. <button type="button" onClick={() => navigate("/")} className="text-teal-600 font-semibold">Back to all systems</button></p>}
          </form>
        ) : (
          <div className="animate-slide-in bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
            <button onClick={() => { setPhase("phone"); setPin(""); setError(""); }} className="text-slate-400 text-sm mb-3">← use a different number</button>
            <div className="text-center mb-4">
              <div className="w-14 h-14 rounded-full bg-teal-100 text-teal-700 grid place-items-center font-heading font-bold text-xl mx-auto mb-2">{initial(matched?.name)}</div>
              <div data-testid="login-greeting" className="font-heading font-bold text-slate-800 text-lg">G'day, {matched?.name} 👋</div>
              <div className="text-slate-400 text-sm">Enter your 4-digit PIN</div>
            </div>
            <div className="flex justify-center gap-3 mb-4" data-testid="pin-display">
              {[0, 1, 2, 3].map((i) => (<div key={i} className={`w-4 h-4 rounded-full transition-colors ${pin.length > i ? "bg-teal-600" : "bg-slate-200"}`} />))}
            </div>
            {error && <p data-testid="login-error" className="text-rose-500 text-sm text-center mb-3">{error}</p>}
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                <button key={d} data-testid={`pin-key-${d}`} onClick={() => tap(String(d))} className="h-18 py-5 bg-slate-50 active:bg-teal-100 active:scale-95 transition-transform rounded-2xl font-mono text-3xl text-slate-800 font-medium">{d}</button>
              ))}
              <div />
              <button data-testid="pin-key-0" onClick={() => tap("0")} className="h-18 py-5 bg-slate-50 active:bg-teal-100 active:scale-95 transition-transform rounded-2xl font-mono text-3xl text-slate-800 font-medium">0</button>
              <button data-testid="pin-key-del" onClick={() => setPin(pin.slice(0, -1))} className="h-18 py-5 bg-slate-100 active:scale-95 transition-transform rounded-2xl grid place-items-center text-slate-500"><Delete strokeWidth={2.5} /></button>
            </div>
          </div>
        )}
        <p className="text-center text-slate-300 text-xs mt-6">Ready2Work AU · Production management &amp; performance</p>
      </div>
    </div>
  );
}
